import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isAdminRole, isStaffRole } from '../../utils/roles';
import NavSearchBar from './NavSearchBar';
import UserMenuDropdown from './UserMenuDropdown';
import MobileDrawer from './MobileDrawer';
import CategoriesMenu from './CategoriesMenu';
import BrandLogo from '../brand/BrandLogo';
import {
  MAIN_NAV_LINKS,
  SOCIAL_LINKS,
  SUPPORT_EMAIL,
  CONTACT_PATH,
  TOP_BAR_LINKS,
  getListEventPath,
  getDashboardPath
} from '../../constants/siteNav';

const SiteHeader = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const profileBtnRef = useRef(null);

  useEffect(() => {
    setShowUserMenu(false);
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    navigate('/');
  };

  const firstName = (user?.name || 'Account').split(' ')[0];

  const isActive = (path) =>
    path === '/'
      ? location.pathname === '/'
      : location.pathname === path || location.pathname.startsWith(`${path}/`);

  const navLinkClass = (path) =>
    `px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
      isActive(path)
        ? 'text-orange-600 bg-orange-50'
        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
    }`;

  const topBarLinkClass = (path) =>
    `text-[11px] font-medium normal-case tracking-normal transition-colors ${
      isActive(path) ? 'text-orange-400' : 'text-gray-300 hover:text-orange-400'
    }`;

  const mainLinks = MAIN_NAV_LINKS.filter((l) => l.to !== '/categories');

  return (
    <>
      <div className="sticky top-0 left-0 right-0 z-[200] m-0 p-0 bg-white" data-site-header>
        {/* Top bar - fully removed when scrolled so nothing peeks above the nav */}
        <div
          className={
            scrolled
              ? 'hidden'
              : 'hidden sm:block bg-gray-900 text-gray-300 border-b border-gray-800'
          }
        >
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between h-9 text-[11px]">
              <div className="flex items-center gap-4 min-w-0">
                <span className="text-gray-400 font-medium truncate hidden md:inline">
                  Kenya&apos;s premier event platform
                </span>
                <span className="hidden md:inline text-gray-700" aria-hidden>·</span>
                <nav className="flex items-center gap-4 shrink-0" aria-label="Support">
                  {TOP_BAR_LINKS.map((link) => (
                    <Link key={link.to} to={link.to} className={topBarLinkClass(link.to)}>
                      {link.label}
                    </Link>
                  ))}
                </nav>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="hidden lg:inline text-[11px] font-medium text-gray-400 hover:text-orange-400 transition-colors"
                >
                  {SUPPORT_EMAIL}
                </a>
                <span className="hidden lg:inline text-gray-700" aria-hidden>·</span>
                <div className="flex items-center gap-3.5">
                  {SOCIAL_LINKS.map((social) => (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-orange-400 transition-colors"
                      aria-label={social.label}
                    >
                      <i className={`${social.icon} text-xs`} />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main navigation */}
        <header
          className={`m-0 bg-white border-b transition-shadow duration-300 ${
            scrolled ? 'border-gray-200 shadow-md' : 'border-gray-200/70 shadow-sm'
          }`}
        >
          <div className="container mx-auto px-4">
            {/* Mobile / tablet - menu | centered logo | search (desktop unchanged below) */}
            <div
              className={`relative flex lg:hidden items-center justify-between transition-all duration-300 ${
                scrolled ? 'h-14' : 'h-16'
              }`}
            >
              <button
                type="button"
                className="w-10 h-10 flex items-center justify-center text-gray-900 hover:bg-gray-100 rounded-md transition-colors -ml-1"
                onClick={() => setMobileOpen(true)}
                aria-label="Open menu"
              >
                <i className="fas fa-bars text-lg" />
              </button>

              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-auto">
                <BrandLogo
                  size="lg"
                  className="justify-center"
                  imgClassName="shadow-sm rounded-md"
                  priority
                />
              </div>

              <button
                type="button"
                className="w-10 h-10 flex items-center justify-center text-gray-900 hover:bg-gray-100 rounded-md transition-colors -mr-1"
                onClick={() => navigate('/events')}
                aria-label="Browse events"
              >
                <i className="fas fa-calendar-alt text-base" />
              </button>
            </div>

            {/* Desktop - existing layout */}
            <div
              className={`hidden lg:flex items-center gap-3 lg:gap-4 transition-all duration-300 ${
                scrolled ? 'h-[60px]' : 'h-[76px]'
              }`}
            >
              {/* Logo */}
              <BrandLogo
                size={scrolled ? 'md' : 'lg'}
                className="transition-all duration-300"
                imgClassName="shadow-sm group-hover:shadow-md transition-shadow"
                priority
              />

              {/* Center search */}
              <div className="flex flex-1 justify-center min-w-0 px-2">
                <NavSearchBar expanded />
              </div>

              {/* Nav links */}
              <nav className="flex items-center gap-0.5 shrink-0" aria-label="Main">
                {mainLinks.map((link) => (
                  <Link key={link.to} to={link.to} className={navLinkClass(link.to)}>
                    {link.label}
                  </Link>
                ))}
                <CategoriesMenu isActive={isActive} />
                <Link
                  to={CONTACT_PATH}
                  className={`group inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                    isActive(CONTACT_PATH)
                      ? 'text-orange-600 bg-orange-50'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <i className="fas fa-headset text-[13px] text-gray-500 group-hover:text-orange-500 transition-colors" aria-hidden />
                  Contact
                </Link>
              </nav>

              {/* Actions */}
              <div className="flex items-center gap-2 shrink-0">
                <Link
                  to={getListEventPath(user)}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-semibold text-orange-600 border border-orange-300/80 rounded-md hover:bg-orange-50 hover:border-orange-400 transition-all"
                >
                  <i className="fas fa-plus text-[10px]" />
                  List your event
                </Link>

                {isAuthenticated ? (
                  <>
                    <button
                      ref={profileBtnRef}
                      type="button"
                      onClick={() => setShowUserMenu((open) => !open)}
                      className="flex items-center gap-2 px-2 py-1 border border-gray-200 rounded-md hover:border-orange-300 hover:bg-orange-50/60 transition-all max-w-[140px]"
                      aria-expanded={showUserMenu}
                      aria-haspopup="true"
                      aria-label="Account menu"
                    >
                      {user?.avatar ? (
                        <img
                          src={user.avatar}
                          alt=""
                          className={`rounded-md object-cover shrink-0 transition-all duration-300 ${
                            scrolled ? 'w-7 h-7' : 'w-8 h-8'
                          }`}
                        />
                      ) : (
                        <span
                          className={`rounded-md bg-orange-100 text-orange-600 flex items-center justify-center shrink-0 transition-all duration-300 ${
                            scrolled ? 'w-7 h-7' : 'w-8 h-8'
                          }`}
                        >
                          <i className="fas fa-user text-xs" />
                        </span>
                      )}
                      <span className="text-sm font-medium text-gray-700 truncate">
                        {firstName}
                      </span>
                      <i className={`fas fa-chevron-down text-[9px] text-gray-400 shrink-0 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                    </button>

                    <UserMenuDropdown
                      open={showUserMenu}
                      anchorRef={profileBtnRef}
                      onClose={() => setShowUserMenu(false)}
                    >
                      <div className="px-3 py-2.5 border-b border-gray-100 mb-1 bg-gray-50/50 rounded-t-md">
                        <p className="text-sm font-semibold text-gray-900 truncate">{user?.name}</p>
                        <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                      </div>
                      <Link
                        to={isStaffRole(user?.role) ? '/dashboard' : getDashboardPath(user)}
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 hover:bg-orange-50/80"
                        role="menuitem"
                      >
                        <i className="fas fa-ticket-alt w-4 text-orange-400 text-xs" />
                        My tickets
                      </Link>
                      <Link
                        to="/dashboard"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 hover:bg-orange-50/80"
                        role="menuitem"
                      >
                        <i className="fas fa-user w-4 text-gray-400 text-xs" />
                        Account
                      </Link>
                      {isStaffRole(user?.role) && (
                        <>
                          <Link
                            to="/admin/sales"
                            onClick={() => setShowUserMenu(false)}
                            className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 hover:bg-orange-50/80"
                            role="menuitem"
                          >
                            <i className="fas fa-chart-line w-4 text-gray-400 text-xs" />
                            {isAdminRole(user?.role) ? 'Sales / Tickets' : 'Organizer sales'}
                          </Link>
                          <Link
                            to="/admin/events"
                            onClick={() => setShowUserMenu(false)}
                            className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 hover:bg-orange-50/80"
                            role="menuitem"
                          >
                            <i className="fas fa-calendar-alt w-4 text-gray-400 text-xs" />
                            My events
                          </Link>
                          <Link
                            to="/admin/events/create"
                            onClick={() => setShowUserMenu(false)}
                            className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 hover:bg-orange-50/80"
                            role="menuitem"
                          >
                            <i className="fas fa-calendar-plus w-4 text-gray-400 text-xs" />
                            Create event
                          </Link>
                          {isAdminRole(user?.role) && (
                            <Link
                              to="/admin/dashboard"
                              onClick={() => setShowUserMenu(false)}
                              className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 hover:bg-orange-50/80"
                              role="menuitem"
                            >
                              <i className="fas fa-cog w-4 text-gray-400 text-xs" />
                              Admin panel
                            </Link>
                          )}
                        </>
                      )}
                      <div className="border-t border-gray-100 mt-1 pt-1 pb-0.5">
                        <button
                          type="button"
                          onClick={handleLogout}
                          className="flex w-full items-center gap-2.5 px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-b-lg"
                          role="menuitem"
                        >
                          <i className="fas fa-sign-out-alt w-4 text-xs" />
                          Sign out
                        </button>
                      </div>
                    </UserMenuDropdown>
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <Link
                      to="/login"
                      className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-orange-600 transition-colors"
                    >
                      Sign in
                    </Link>
                    <Link
                      to="/register"
                      className="px-4 py-1.5 text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-md transition-colors shadow-sm"
                    >
                      Sign up
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>
      </div>

      <MobileDrawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        user={user}
        isAuthenticated={isAuthenticated}
        isActive={isActive}
        onLogout={handleLogout}
      />
    </>
  );
};

export default SiteHeader;
